import { BusinessSociabilitySaysApproved } from "common/types";
import { Info } from "phosphor-react";
import React, { useMemo } from "react";
import { useI18N } from "common/hooks";

interface VenueNotesProps {
  data: BusinessSociabilitySaysApproved[];
  containerClassName?: string;
  isLoading?: boolean;
  gotoMapperView: () => void;
}

const VenueNotes = ({ data, gotoMapperView }: VenueNotesProps) => {
  const i18n = useI18N();
  const groupedNotes = useMemo(() => {
    if (!data?.length) return [];

    // Create a map to group and count notes by action_title
    const groupsMap = data.reduce(
      (acc, note) => {
        const title = note.action_title || "Other";
        if (!acc[title]) {
          acc[title] = {
            title,
            count: 0,
            notes: [],
          };
        }
        acc[title].count += 1;
        acc[title].notes.push(note);
        return acc;
      },
      {} as Record<
        string,
        {
          title: string;
          count: number;
          notes: BusinessSociabilitySaysApproved[];
        }
      >
    );

    // Convert to array and filter out empty groups
    return Object.values(groupsMap).filter((group) => group.count > 0);
  }, [data]);

  const summaryText = useMemo(() => {
    return groupedNotes
      .map((group) => `${group.count} ${group.title}`)
      .join(" • ");
  }, [groupedNotes]);

  if (data?.length === 0) {
    return null;
  }

  return (
    <section className="mb-4">
      <div className="bg-primary-50 dark:bg-[#313A71] border border-primary-200 rounded-xl p-3">
        <div className="flex">
          {/* Icon column */}
          <div className="flex-shrink-0 mr-3">
            <Info
              size={28}
              className="!text-primary-500 dark:!text-primary-300"
            />
          </div>
          <div className="flex-1">
            {/* Notice count */}
            <div className="mb-1">
              <span className="paragraph-small-semibold text-primary-800 dark:text-primary-50">
                {summaryText}
              </span>
            </div>

            <div className="line-clamp-2">
              {data.length > 1 ? (
                <p className="ucfirst paragraph-small-medium text-primary-800 dark:text-primary-50 line-clamp-2">
                  {data.map((e) => e.business_note_title).join(", ")}
                </p>
              ) : (
                <div>
                  <p className="paragraph-small-medium text-primary-800 dark:text-primary-50">
                    {data[0].business_note_title +
                      " - " +
                      data[0].business_note_text}
                  </p>
                </div>
              )}
            </div>
            <p
              className="paragraph-small-medium text-primary-500 dark:text-primary-300 mt-1 cursor-pointer"
              onClick={gotoMapperView}
            >
              {i18n.get("viewNoteDetails")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueNotes;
